import {
  AbilityBuilder,
  PureAbility,
  subject as subjectHelper,
  type MatchConditions
} from '@casl/ability'
import { Role } from './lib/role'
import { useSharedWorker } from '@/composables/useSharedworker'
import { ADMIN_RULES } from './rules/adminRules'
import { LEADER_RULES } from './rules/leaderRules'
import { MANAGER_RULES } from './rules/managerRules'
import { VIEWER_MEMBER_RULES } from './rules/viewerMemberRules'
import { MEMBER_RULES } from './rules/memberRules'
import { z } from 'zod'
import {
  Actions,
  type AppAbility,
  type SubjectResolver,
  type RuleEntry,
  type AllSubjects,
  type AllSubjectsWithCondition,
  type CompleteRuleSet
} from './rules/abilityTypes'

const Worker = useSharedWorker()

const lambdaMatcher = (matchConditions: MatchConditions) => matchConditions

const RULE_SETS_BY_ROLE = {
  [Role.Admin]: ADMIN_RULES,
  [Role.Leader]: LEADER_RULES,
  [Role.Manager]: MANAGER_RULES,
  [Role.ViewerMember]: VIEWER_MEMBER_RULES,
  [Role.Member]: MEMBER_RULES,
  [Role.Unknown]: {} as CompleteRuleSet
} as const

type ResolverKey = `${Actions}:${AllSubjects}`
type ResolversMap = { [K in ResolverKey]?: SubjectResolver }

function defineRulesFor(role: Role, userId: string): Array<RuleEntry & { subject: AllSubjects }> {
  const ruleSet = RULE_SETS_BY_ROLE[role]
  return Object.entries(ruleSet).flatMap(([subject, factory]) =>
    factory(userId).map((rule: RuleEntry) => ({ ...rule, subject }))
  )
}

export function buildAbilityFor(
  role: Role,
  userId: string
): { ability: AppAbility; resolvers: ResolversMap } {
  const rulesForContext = defineRulesFor(role, userId)
  const { can, build } = new AbilityBuilder<
    PureAbility<[Actions, AllSubjectsWithCondition], MatchConditions>
  >(PureAbility)
  const resolvers: ResolversMap = {}

  for (const rule of rulesForContext) {
    for (const action of rule.actions) {
      if (rule.condition !== undefined) {
        can(action, rule.subject, rule.condition)
      } else {
        can(action, rule.subject)
      }
      if (rule.fetch) {
        resolvers[createResolverKey(action, rule.subject)] = rule.fetch
      }
    }
  }

  const generatedAbility = build({
    conditionsMatcher: lambdaMatcher
  })

  return {
    ability: generatedAbility as AppAbility,
    resolvers: resolvers
  }
}

function createResolverKey(action: Actions, subject: AllSubjects): ResolverKey {
  return `${action}:${subject}`
}

export async function can(
  action: Actions,
  subject: AllSubjects,
  params: Record<string, string | string[]> | undefined,
  resolvers: ResolversMap,
  ability: AppAbility
): Promise<boolean> {
  try {
    const resolver = resolvers[createResolverKey(action, subject)]
    if (resolver) {
      if (params === undefined) {
        // CASLはconditionに渡すObjectがnullの場合、常にtrueを返す。
        // そのため、resolverがある=conditionがあるということなので、paramsが無い場合、falseとする
        return false
      }
      await Worker.waitState('SyncRunning')

      const safeParams = z.record(z.string().optional()).parse(params)
      const resolvedObj = await resolver(safeParams)
      const subjectWithAttrs = subjectHelper(subject, resolvedObj)
      return ability.can(action, subjectWithAttrs)
    }

    return ability.can(action, subject)
  } catch (error) {
    console.error('Error in checkPermission:', error)
    return false
  }
}
